import '../styles/pdf-pages.css';
import '../styles/model-overview.css';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateVenueCo2 } from '@/domain/impact-calculate-venue/entities/calculate-venue-co2.entity';
import { CalculateVenueEnv } from '@/domain/impact-calculate-venue/entities/calculate-venue-env.entity';
import {
  selectImpactCalculateVenueCo2ForProject,
  selectImpactCalculateVenueEnvForProject,
} from '@/domain/impact-calculate-venue/impact-calculate-venue.selectors';
import { ProjectVenue } from '@/domain/project-venue/entities/project-venue.entity';
import { selectCurrentProjectVenue } from '@/domain/project-venue/project-venue.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { formatProjectDatesTz } from '@/ui/utils/format-project-dates';

import CalendarMonthSvg from '../assets/calendar_month.svg';
import { LittleGauge } from '../components/LittleGauge';
import { PdfProjectPager } from '../components/PdfProjectPager';
import { PdfScoreCard } from '../components/PdfScoreCard';
import { extendVenueEnvCalculate } from '../utils/compute-extend-env';
import { displayDecimal } from '../utils/display-scores.utils';

export function PdfVenuePage3({ totalPages }: { totalPages: number }) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const venue = useAppSelector(
    selectCurrentProjectVenue,
  ) satisfies ProjectVenue | null;
  const venueId = venue?.id ?? 0;

  const impactEnv: CalculateVenueEnv = extendVenueEnvCalculate(
    useAppSelector(selectImpactCalculateVenueEnvForProject(venueId)),
  );

  const impactCo2: CalculateVenueCo2 = useAppSelector(
    selectImpactCalculateVenueCo2ForProject(venueId),
  );

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!venue) {
    return <h1>{translate('PdfExports.venue-not-found')}</h1>;
  }

  const impactEnvScore100 =
    Math.round((impactEnv.global.score * 100) / impactEnv.global.maxScore) || 0;

  return (
    <div id="page3" className="pdf-page">
      <div className="content">
        <div className="pdf-light-header">
          <div>
            <h1>{venue.name}</h1>
            {venue.startDate && venue.endDate && (
              <h2>
                <img
                  src={CalendarMonthSvg}
                  width="19"
                  height="19"
                  style={{ position: 'relative', top: 4, marginRight: 5 }}
                  alt="Calendar icon"
                />
                <span className="festival-date">
                  {formatProjectDatesTz(
                    venue.startDate || '',
                    venue.endDate || '',
                  )}
                </span>
              </h2>
            )}
          </div>
        </div>
        <div className="pdf-separator"></div>

        <div className="model-header">
          <div>
            <h2>{translate('PdfExports.env-score-details')}</h2>
          </div>
          <div>
            <h4>
              <span className="highlight">{impactEnvScore100}</span>/ 100
            </h4>
            <h5>
              {impactEnv.global.score} /{' '}
              {displayDecimal(impactEnv.global.maxScore)}
            </h5>
          </div>
          <div>
            <PdfScoreCard
              type="env"
              scoreLetter={impactEnv.global.scoreLetter}
              size="medium"
            />
          </div>
        </div>

        <div
          className="model-content"
          style={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            width: 700,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '15px',
              flexWrap: 'wrap',
            }}
          >
            <div className="sector" style={{ maxWidth: '345px' }}>
              <div className="sector-header">
                <h4>{translate('Projects.co2')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(impactCo2.global.kgeCO2ByParticipant)}
                  </span>{' '}
                  {translate('Projects.kgeCO2-by-participant')}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  <li>{translate('PdfExports.co2-emissons-tipnote')}</li>
                </ul>
                <div
                  style={{
                    width: '160px',
                    paddingTop: '20px',
                    paddingLeft: '50px',
                  }}
                >
                  <PdfScoreCard
                    type="co2"
                    scoreLetter={impactCo2.global.scoreLetter}
                    size="medium"
                  />
                </div>
              </div>
            </div>

            <div className="sector" style={{ maxWidth: '345px' }}>
              <div className="sector-header">
                <h4>{translate('Env.Topics.PUBLIC_ENGAGEMENT')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(
                      impactEnv.structure.topics.PUBLIC_ENGAGEMENT.score,
                    )}
                  </span>{' '}
                  /{' '}
                  {displayDecimal(
                    impactEnv.structure.topics.PUBLIC_ENGAGEMENT.maxScore,
                  )}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      impactEnv.structure.topics.PUBLIC_ENGAGEMENT.sections,
                    ) as [
                      keyof typeof impactEnv.structure.topics.PUBLIC_ENGAGEMENT.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Env.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {displayDecimal(maxScore)}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={impactEnv.structure.topics.PUBLIC_ENGAGEMENT.score}
                  maxScore={
                    impactEnv.structure.topics.PUBLIC_ENGAGEMENT.maxScore
                  }
                />
              </div>
            </div>

            <div className="sector" style={{ maxWidth: '345px' }}>
              <div className="sector-header">
                <h4>{translate('Env.Topics.FOOD_BEVERAGES')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(
                      impactEnv.structure.topics.FOOD_BEVERAGES.score,
                    )}
                  </span>{' '}
                  /{' '}
                  {displayDecimal(
                    impactEnv.structure.topics.FOOD_BEVERAGES.maxScore,
                  )}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      impactEnv.structure.topics.FOOD_BEVERAGES.sections,
                    ) as [
                      keyof typeof impactEnv.structure.topics.FOOD_BEVERAGES.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Env.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {displayDecimal(maxScore)}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={impactEnv.structure.topics.FOOD_BEVERAGES.score}
                  maxScore={impactEnv.structure.topics.FOOD_BEVERAGES.maxScore}
                />
              </div>
            </div>

            <div className="sector" style={{ maxWidth: '345px' }}>
              <div className="sector-header">
                <h4>{translate('Env.Topics.COMMUNICATION')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(
                      impactEnv.structure.topics.COMMUNICATION.score,
                    )}
                  </span>{' '}
                  /{' '}
                  {displayDecimal(
                    impactEnv.structure.topics.COMMUNICATION.maxScore,
                  )}
                </div>
              </div>

              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      impactEnv.structure.topics.COMMUNICATION.sections,
                    ) as [
                      keyof typeof impactEnv.structure.topics.COMMUNICATION.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Env.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {displayDecimal(maxScore)}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={impactEnv.structure.topics.COMMUNICATION.score}
                  maxScore={impactEnv.structure.topics.COMMUNICATION.maxScore}
                />
              </div>
            </div>

            <div className="sector" style={{ maxWidth: '345px' }}>
              <div className="sector-header">
                <h4>{translate('Env.Topics.RESOURCES_POLLUTION')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(
                      impactEnv.structure.topics.RESOURCES_POLLUTION.score,
                    )}
                  </span>{' '}
                  /{' '}
                  {displayDecimal(
                    impactEnv.structure.topics.RESOURCES_POLLUTION.maxScore,
                  )}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      impactEnv.structure.topics.RESOURCES_POLLUTION.sections,
                    ) as [
                      keyof typeof impactEnv.structure.topics.RESOURCES_POLLUTION.sections,
                      { score: number; maxScore: number },
                    ][]
                  )
                    .sort(([key1], [key2]) =>
                      key1 === 'INTENSITY' ? -1 : key2 === 'INTENSITY' ? 1 : 0,
                    )
                    .map(([key, { score, maxScore }]) => (
                      <li key={key}>
                        <span>{translate(`Env.Sections.${key}`)}</span>
                        <span>
                          {displayDecimal(score)} / {displayDecimal(maxScore)}
                        </span>
                      </li>
                    ))}
                </ul>
                <LittleGauge
                  score={impactEnv.structure.topics.RESOURCES_POLLUTION.score}
                  maxScore={
                    impactEnv.structure.topics.RESOURCES_POLLUTION.maxScore
                  }
                />
              </div>
            </div>
          </div>

          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              height: '290px',
            }}
          >
            {' '}
            <div className="model-overall-comments">
              {translate('PdfExports.comment')}
            </div>
          </div>
        </div>

        <div className="navigation-controls">
          <a href="#page2" className="tag-pill">
            {translate('PdfExports.go-back-to-co2')}
          </a>
          <a href="#page4" className="tag-pill">
            {translate('PdfExports.go-to-ess')}
          </a>
        </div>
      </div>

      <PdfProjectPager current={3} total={totalPages}></PdfProjectPager>
    </div>
  );
}
